import * as React from 'react';
import * as _ from 'underscore';
import * as $ from 'jquery';

import { IGlob } from '../../../../pdc-platform/src/ts/Glob';
import { ParameterField } from '../../../platform/tsx/ParameterField';
import { ColorBoxField } from '../../../../_common/platform/tsx/ColorBoxField';
import { DiscreteIntervalsKind } from '../../../../_common/platform/tsx/DiscreteIntervals';
import { IValuesCollection } from '../../ts/appTheme';
import { themeVars } from '../../../../pdc-platform/src/ts/themes/themeConstants';
import { IFormatChooserPopupOptions } from './FormatChooserPopup';
import { IMappingValue } from '../../../../_common/platform/tsx/IntervalsSelect';

import SelectField from '../SelectField';
import { FontField } from '../FontField';
import BooleanField from '../BooleanField';
import { MappingType } from '../../ts/Interfaces';
import { IParameterFieldBase } from '../../ts/ParameterFieldBase';

function ColorMappingChooserField(props: {
    mappingName: string;
    mappingType: MappingType;
    prohibitAttributeMapping?: boolean; //LL: 02.12.20   default false
    theme: IValuesCollection;

    onChange(value: IMappingValue): void;
}) {
    const separOptions = '→';
    const colorScalesSuffix: MappingType = 'ColorScale';
    const discreteColorsSuffix: MappingType = 'DiscreteMapping';
    const attributeMappingColorSuffix: MappingType = 'AttributeMapping';

    const getMappingObj = (value) => {
        let mappingName = value;
        let mappingType;
        let n = value.indexOf(separOptions);

        if (n >= 0) {
            mappingType = value.substring(n + 1); //  "ColorScale"/"DiscreteMapping"/"AttributeMapping"
            mappingName = value.substring(0, n);
        } else {
            //LL: for compatibility with old functionality  and 'none'
            if (value !== 'none') {
                mappingType = colorScalesSuffix;
            }
        }

        return {
            mappingName: mappingName,
            mappingType: mappingType
        };
    };

    const handleColorSchemaChange = (value) => {
        const mapping = getMappingObj(value); // { mappingName: mappingName, mappingType: mappingType }
        const { mappingName, mappingType } = mapping;

        if (typeof props.onChange === 'function') {
            props.onChange({
                mappingName: mappingName,
                mappingType: mappingType
            });
        }
    };

    const theme = props.theme;

    //  -- ColorScales:
    //LL: reduced by mapping="linear" && scaleMethod="automatic" && scaleAuto=true
    //   mapping="discrete"/"linear", scaleMethod="automatic"/"defineRange", scaleAuto=true/false (scaleMin, scaleMax)
    //LL:  <optgroup> for each MappingType (now only ColorScales):   <option> must be inside <optgroup>
    let arrColorScales = [];

    if (theme != null) {
        arrColorScales = theme[themeVars.color_scales_set];
    }

    let colorSchemeOptions_Extended = _.map(arrColorScales, (obj, i) => {
        //EVG: See Issue 95479. Victor suggested to drop this check.
        // //LL: reduced by mapping="linear" && scaleMethod="automatic" && scaleAuto=true
        // if (obj.mapping === 'linear' && obj.scaleMethod === 'automatic' && obj.scaleAuto === true) {
        //     return null;
        // }

        let name = obj.name;

        return (
            <option key={name + '-' + i} value={name + separOptions + colorScalesSuffix}>
                {name}
            </option>
        );
    });

    //  -- for Discrete Colors:
    let arrDiscreteColors = [];

    if (theme) {
        arrDiscreteColors = theme[themeVars.discreteMapping_set];
    }

    let discreteColorsOptions_Extended = _.map(arrDiscreteColors, (obj, i) => {
        const resultingType = obj.resultingType;

        if (resultingType !== DiscreteIntervalsKind.color) {
            return null;
        }

        const name = obj.name;
        //  disabled={ true }  - may be used for now
        return (
            <option key={name + '-' + i} value={name + separOptions + discreteColorsSuffix}>
                {name}
            </option>
        );
    });

    //  -- for Attribute Color Mapping:
    let attributeMappingColorGroupElement: React.JSX.Element;

    if (props.prohibitAttributeMapping != true) {
        //LL: 02.12.20: prohibitAttributeMapping   default false
        attributeMappingColorGroupElement = (
            <optgroup key="attributeMappingColorGroup" label="Attribute Mapping Color">
                <option value={'attributeMapping' + separOptions + attributeMappingColorSuffix}>
                    {'Attribute Mapping'}
                </option>
            </optgroup>
        );
    }

    //  old displayValBackColor
    let displayValColor = props.mappingName || 'none';
    let suffix: MappingType = colorScalesSuffix; //LL:  as OLD

    if (props.mappingType != null) {
        suffix = props.mappingType;
    }

    if (displayValColor !== 'none') {
        displayValColor += separOptions + suffix;
    }

    return (
        <SelectField value={displayValColor} onChange={handleColorSchemaChange}>
            <option key="none" value="none">
                {'none'}
            </option>
            <optgroup key="colorScaleGroup" label="Color Scales">
                {colorSchemeOptions_Extended}
            </optgroup>
            <optgroup key="discreteColorsGroup" label="Discrete Colors">
                {discreteColorsOptions_Extended}
            </optgroup>
            {attributeMappingColorGroupElement}
        </SelectField>
    );
}

function getColorParam(defaultVal, themeVar, theme: IValuesCollection, glob: IGlob): IParameterFieldBase<any> {
    return {
        defaultVal: () => {
            return theme[themeVar] || defaultVal;
        },
        glob: glob,
        options: {
            hideTransparency: true
        }
    };
}

const defaultBackColor = '#FFFFFF';

function BackColorField(props: {
    backgroundColor;
    zIndex: number;
    theme: IValuesCollection;
    glob: IGlob;
    onBackgroundColorChange_ColorBox(value): void;
}) {
    const { theme, glob } = props;
    const paramBgColor = getColorParam(defaultBackColor, 'widget-bg', theme, glob);
    const displayBackColor = props.backgroundColor || (theme && theme['widget-bg']) || defaultBackColor;

    return (
        <ParameterField
            param={paramBgColor}
            label={'Background Color'}
            className="mb-1"
            options={{
                inline: false,
                clearable: true,
                onClear: () => props.onBackgroundColorChange_ColorBox(null)
            }}
            glob={glob}
        >
            <ColorBoxField
                value={displayBackColor}
                themeObj={theme}
                hideTransparency={false}
                showText={true}
                options={{
                    hideTransparency: false
                }}
                param={paramBgColor}
                glob={glob}
                onChange={props.onBackgroundColorChange_ColorBox}
            />
        </ParameterField>
    );
}

function getFontParam(defaultVal, themeVar, theme: IValuesCollection, glob: IGlob): IParameterFieldBase<any> {
    return {
        defaultVal: () => {
            return theme[themeVar] || defaultVal;
        },
        glob: glob
    };
}

const defaultForeColor = '#000000';

function ForeColorField(props: {
    foregroundColor;
    zIndex: number;
    theme: IValuesCollection;
    glob: IGlob;
    onForegroundColorChange_ColorBox(value): void;
}) {
    const { theme, glob } = props;
    const paramFgColor = getColorParam(defaultForeColor, 'gray-base', theme, glob);
    const displayForeColor = props.foregroundColor || (theme && theme['gray-base']) || defaultForeColor;

    return (
        <ParameterField
            param={paramFgColor}
            label={'Foreground Color'}
            className="mb-1"
            options={{
                inline: false,
                clearable: true,
                onClear: () => props.onForegroundColorChange_ColorBox(null)
            }}
            glob={glob}
        >
            <ColorBoxField
                value={displayForeColor}
                themeObj={theme}
                hideTransparency={true}
                showText={true}
                options={{
                    hideTransparency: true
                }}
                param={paramFgColor}
                glob={glob}
                onChange={props.onForegroundColorChange_ColorBox}
            />
        </ParameterField>
    );
}

const defaultFontFace = null;

function FontFaceField(props: { fontFace; theme: IValuesCollection; glob: IGlob; onChangeFontFace(value): void }) {
    const { theme, glob } = props;
    const fontFaceParam = getFontParam(defaultFontFace, '#font-main', theme, glob);
    const displayFontFace = props.fontFace || (theme && theme['#font-main']) || defaultFontFace;

    return (
        <ParameterField
            param={fontFaceParam}
            label={'Font Face'}
            options={{
                inline: false,
                clearable: true,
                onClear: () => props.onChangeFontFace(null)
            }}
            glob={glob}
        >
            <FontField value={displayFontFace} theme={theme} glob={glob} onChange={props.onChangeFontFace} />
        </ParameterField>
    );
}

const defaultFontSize = 'default';

function FontSizeField(props: { fontSize; theme: IValuesCollection; glob: IGlob; onChangeFontSize(value): void }) {
    const { theme, glob } = props;
    const fontSizeParam = getFontParam(defaultFontSize, 'font-main-size', theme, glob);
    const displayFontSize = props.fontSize || (theme && theme['font-main-size']) || defaultFontSize;

    return (
        <ParameterField
            param={fontSizeParam}
            label={'Font Size'}
            options={{
                inline: false,
                clearable: true,
                onClear: () => props.onChangeFontSize(null)
            }}
            glob={glob}
        >
            <SelectField
                value={displayFontSize}
                options={{
                    values: [
                        'default',
                        'xx-small',
                        'x-small',
                        'small',
                        'medium',
                        'large',
                        'x-large',
                        'xx-large',
                        'smaller',
                        'larger'
                    ],
                    display: [
                        'default',
                        'xx-small',
                        'x-small',
                        'small',
                        'medium',
                        'large',
                        'x-large',
                        'xx-large',
                        'smaller',
                        'larger'
                    ]
                }}
                onChange={props.onChangeFontSize}
            />
        </ParameterField>
    );
}

const defaultFontWeight = 'normal';

function FontWeightField(props: {
    fontWeight;
    theme: IValuesCollection;
    glob: IGlob;
    onChangeFontWeight(value): void;
}) {
    const { theme, glob } = props;
    const fontWeightParam = getFontParam(defaultFontWeight, 'font-main-weight', theme, glob);
    const displayFontWeight = props.fontWeight || (theme && theme['font-main-weight']) || defaultFontWeight;

    return (
        <ParameterField
            param={fontWeightParam}
            label={'Font Weight'}
            options={{
                inline: false,
                clearable: true,
                onClear: () => props.onChangeFontWeight(null)
            }}
            glob={glob}
        >
            <SelectField
                value={displayFontWeight}
                options={{
                    values: ['lighter', 'normal', 'bold', 'bolder'],
                    display: ['lighter', 'normal', 'bold', 'bolder']
                }}
                onChange={props.onChangeFontWeight}
            />
        </ParameterField>
    );
}

const defaultFontItalic = false;

function FontItalicField(props: {
    fontItalic;
    theme: IValuesCollection;
    glob: IGlob;
    onChangeFontItalic(value): void;
}) {
    const { theme, glob } = props;
    const fontItalicParam = getFontParam(defaultFontItalic, 'font-main-italic', theme, glob);
    const displayFontItalic = props.fontItalic || (theme && theme['font-main-italic']) || defaultFontItalic;

    return (
        <ParameterField
            param={fontItalicParam}
            options={{
                inline: true,
                clearable: true,
                noTitle: true,
                onClear: () => props.onChangeFontItalic(null)
            }}
            glob={glob}
        >
            <BooleanField
                value={displayFontItalic}
                title={'Italic'}
                //param={ fontItalicParam } //EVG: There is no setting of style by param here. If is needed, use options.
                onChange={props.onChangeFontItalic}
            />
        </ParameterField>
    );
}

export default function StyleChooser(props: {
    options: IFormatChooserPopupOptions;
    zIndex: number;
    glob: IGlob;

    backgroundColor;
    foregroundColor;
    fontFace;
    fontSize;
    fontWeight;
    fontItalic;
    backgroundColorSchema;
    background_mappingType;
    foregroundColorSchema;
    foreground_mappingType;
    alignment;

    onBackgroundColorChange_ColorBox(value): void;
    onForegroundColorChange_ColorBox(value): void;
    onChangeFontFace(value): void;
    onChangeFontSize(value): void;
    onChangeFontWeight(value): void;
    onChangeFontItalic(value): void;
    onAlignmentChange(value): void;
    onBackColorMappingChange(value: IMappingValue): void;
    onForeColorMappingChange(value: IMappingValue): void;
}) {
    const { glob } = props;
    const theme = props.options.theme;

    let zIndex: number;

    if (props.zIndex != null) {
        zIndex = props.zIndex + 1;
    }

    let alignmentSelector: React.JSX.Element;

    if (props.options.showAlignmentSelector) {
        alignmentSelector = (
            <div className="parameter-field mt-2">
                <ParameterField title={'Alignment'} clearable={false}>
                    <SelectField value={props.alignment || 'default'} onChange={props.onAlignmentChange}>
                        <option value="default">{'default'}</option>
                        <option value="right">{'right'}</option>
                        <option value="center">{'center'}</option>
                        <option value="left">{'left'}</option>
                    </SelectField>
                </ParameterField>
            </div>
        );
    }

    return (
        <div className="popover-content">
            <div className="card mt-1">
                <div className="card-body p-1">
                    <div className="d-flex">
                        <div className="flex-fill" style={{ minWidth: '200px' }}>
                            <BackColorField
                                backgroundColor={props.backgroundColor}
                                zIndex={zIndex}
                                theme={theme}
                                glob={glob}
                                onBackgroundColorChange_ColorBox={props.onBackgroundColorChange_ColorBox}
                            />
                        </div>
                        <div className="flex-fill parameter-field ml-2">
                            <ParameterField title={'Use Color Mapping'} clearable={false}>
                                <ColorMappingChooserField
                                    mappingName={props.backgroundColorSchema}
                                    mappingType={props.background_mappingType}
                                    prohibitAttributeMapping={props.options.isExpression === true}
                                    theme={theme}
                                    onChange={props.onBackColorMappingChange}
                                ></ColorMappingChooserField>
                            </ParameterField>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card mt-1">
                <div className="card-body p-1">
                    <div className="d-flex">
                        <div className="flex-fill" style={{ minWidth: '200px' }}>
                            <ForeColorField
                                foregroundColor={props.foregroundColor}
                                zIndex={zIndex}
                                theme={theme}
                                glob={glob}
                                onForegroundColorChange_ColorBox={props.onForegroundColorChange_ColorBox}
                            />
                        </div>

                        <div className="flex-fill parameter-field ml-2">
                            <ParameterField title={'Use Color Mapping'} clearable={false}>
                                <ColorMappingChooserField
                                    mappingName={props.foregroundColorSchema}
                                    mappingType={props.foreground_mappingType}
                                    prohibitAttributeMapping={props.options.isExpression === true}
                                    theme={theme}
                                    onChange={props.onForeColorMappingChange}
                                ></ColorMappingChooserField>
                            </ParameterField>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card mt-1">
                <div className="card-header p-2">{'Font'}</div>
                <div className="card-body p-1">
                    <div className="d-flex">
                        <div className="flex-fill align-flex-center">
                            <FontFaceField
                                fontFace={props.fontFace}
                                theme={theme}
                                glob={glob}
                                onChangeFontFace={props.onChangeFontFace}
                            />
                        </div>
                        <div className="flex-fill align-flex-center ml-2">
                            <FontWeightField
                                fontWeight={props.fontWeight}
                                theme={theme}
                                glob={glob}
                                onChangeFontWeight={props.onChangeFontWeight}
                            />
                        </div>
                        <div className="flex-fill align-flex-center ml-2">
                            <FontSizeField
                                fontSize={props.fontSize}
                                theme={theme}
                                glob={glob}
                                onChangeFontSize={props.onChangeFontSize}
                            />
                        </div>
                    </div>
                    <div>
                        <FontItalicField
                            fontItalic={props.fontItalic}
                            theme={theme}
                            glob={glob}
                            onChangeFontItalic={props.onChangeFontItalic}
                        />
                    </div>
                </div>
            </div>

            {alignmentSelector}
        </div>
    );
}
